import { createContext, useContext, useEffect, useRef, useState } from "react";
import { apiGet, apiPost } from "../services/Api/apiCall";
import { Call, Device } from "@twilio/voice-sdk";
import { translate } from "../services/Translations/translatorService";
import { useCase } from "./CaseProvider";
import { Activity } from "../types/Activity";
import { CalendlyEvent } from "../types/Calendly/CalendlyEvent";
import { ProductUnion } from "../types/ProductUnion";
import { PhoneCall } from "../types/PhoneCall";
import { HydraCollectionResult } from "../types/HydraCollectionResult";
import { getCalendlyEventReference, getUserConsent, hasLegacyUserConsent, hasUserConsent } from "../services/calendlyService";
import { fetchCollection } from "../services/ReactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../services/ReactQuery/reactQueryService";

type CallEndCallback = () => void;
type CallSession = {
  call: Call | null;
  cancelled: boolean;
  failed: boolean;
  releaseLock: (() => void) | null;
  onEnd: CallEndCallback | null;
  product: ProductUnion;
  phoneCall: Pick<PhoneCall, "id" | "recordingEnabled"> | null;
  isCaseCall: boolean;
  finished: boolean;
  opening: number;
  recordingStopped: boolean;
};

const getTwilioErrorMessage = (error: unknown) => {
  if (!(error instanceof Error)) {
    return "Unbekannter Fehler";
  }

  const code = "code" in error && typeof error.code === "number" ? ` (Twilio ${error.code})` : "";
  return `${error.message}${code}`;
};

type CallContextType = {
  status: string;
  phoneError: string;
  makeCall: (phoneNumber: string, recipientIdentifier?: string | null) => Promise<void>;
  hangUp: () => void;
  cancelRecording: () => Promise<void>;
  recordingEnabled: boolean;
  setRecordingEnabled: (enabled: boolean) => void;
  isConnecting: boolean;
  isStoppingRecording: boolean;
  call: Call | null;
  device: Device | null;
  callModalOpen: boolean;
  openCallModal: (activity: Activity | null, callback?: CallEndCallback) => Promise<void>;
  openCaseCallModal: () => Promise<void>;
  caseCallToDocumentId: number | null;
  closeCallModal: () => void;
  activity: Activity | null;
  calendlyEvent: CalendlyEvent | null;
  callProduct: ProductUnion | null;
  muted: boolean;
  mute: () => void;
  unmute: () => void;
  toggleMute: () => void;
};

const CallContext = createContext<CallContextType | null>(null);

export const CallProvider = ({ children }: { children: React.ReactNode }) => {
  const [device, setDevice] = useState<Device | null>(null);
  const [recordingEnabled, setRecordingEnabled] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [status, setStatus] = useState(translate("call.callStatus.initializing"));
  const [call, setCall] = useState<Call | null>(null);
  const [activity, setActivity] = useState<Activity | null>(null);
  const [calendlyEvent, setCalendlyEvent] = useState<CalendlyEvent | null>(null);
  const [callProduct, setCallProduct] = useState<ProductUnion | null>(null);
  const [phoneError, setPhoneError] = useState("");
  const [muted, setMuted] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isStoppingRecording, setIsStoppingRecording] = useState(false);
  const [caseCallToDocumentId, setCaseCallToDocumentId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const runtime = useRef({
    device: null as Device | null,
    devicePromise: null as Promise<Device> | null,
    session: null as CallSession | null,
    onEnd: null as CallEndCallback | null,
    opening: 0,
    disposed: false,
  });
  const { product } = useCase();

  // The SDK device and pending media requests are owned by this mounted provider.
  useEffect(() => {
    const state = runtime.current;
    state.disposed = false;
    return () => {
      state.disposed = true;
      state.opening += 1;
      if (state.session) {
        state.session.cancelled = true;
        state.session.onEnd = null;
        state.session.call?.disconnect();
        state.session.releaseLock?.();
        state.session = null;
      }
      state.device?.destroy();
      state.device = null;
    };
  }, []);

  const finishCall = (session: CallSession) => {
    session.releaseLock?.();
    session.releaseLock = null;
    if (session.finished) {
      return;
    }
    session.finished = true;
    if (session.isCaseCall && session.phoneCall) {
      apiPost("/twilio/end_case_call", { phoneCallId: session.phoneCall.id, canceled: session.cancelled }).catch(() => {
        if (!runtime.current.disposed && session.opening === runtime.current.opening) {
          setPhoneError("Der Anrufstatus konnte nicht aktualisiert werden. Bitte die Nachbearbeitung über Telefonate öffnen.");
        }
      }).finally(() => queryClient.invalidateQueries({ queryKey: queryKeys.collections("phone_calls") }));
    } else if (session.isCaseCall) {
      // A failed response can still belong to an attempt the server has already persisted.
      queryClient.invalidateQueries({ queryKey: queryKeys.collections("phone_calls") });
    }
    if (runtime.current.session !== session || runtime.current.disposed) {
      return;
    }
    runtime.current.session = null;
    setCall(null);
    setIsConnecting(false);
    setMuted(false);
    if (!session.failed) {
      setStatus(translate("call.callStatus.ended"));
    }
    if (session.isCaseCall && session.phoneCall) {
      const id = session.phoneCall.id;
      setCaseCallToDocumentId(id);
      setCallModalOpen(true);
    }
    if (session.call) {
      session.onEnd?.();
      session.onEnd = null;
    }
  };

  const createTwilioDevice = (): Promise<Device> => {
    const state = runtime.current;
    if (state.device && state.device.state !== Device.State.Destroyed) {
      return Promise.resolve(state.device);
    }
    if (state.devicePromise) {
      return state.devicePromise;
    }
    state.devicePromise = apiPost("/twilio/get_token", {}).then((response) => {
      if (state.disposed) {
        throw new Error("Der Anrufdialog wurde geschlossen.");
      }
      const newDevice = new Device(response.token, { logLevel: "error", enableImprovedSignalingErrorPrecision: true });
      newDevice.on("error", (error: unknown) => {
        if (state.device === newDevice && !state.disposed) {
          setPhoneError(getTwilioErrorMessage(error));
        }
      });
      newDevice.on("tokenWillExpire", async () => {
        try {
          const refreshed = await apiPost("/twilio/get_token", {});
          if (state.device === newDevice && newDevice.state !== Device.State.Destroyed && !state.disposed) {
            newDevice.updateToken(refreshed.token);
          }
        } catch (error) {
          if (!state.disposed) {
            setPhoneError(getTwilioErrorMessage(error));
          }
        }
      });
      state.device = newDevice;
      setDevice(newDevice);
      setStatus(translate("call.callStatus.ready"));
      return newDevice;
    }).finally(() => {
      state.devicePromise = null;
    });
    return state.devicePromise;
  };

  const openCallModal = async (nextActivity: Activity | null, callback?: CallEndCallback) => {
    if (runtime.current.session) {
      setCallModalOpen(true);
      return;
    }
    const reference = nextActivity ? getCalendlyEventReference(nextActivity.calendlyEvent) : null;
    if (!product || !nextActivity || !reference) {
      return;
    }
    const opening = ++runtime.current.opening;
    try {
      const event = await apiGet<CalendlyEvent>(reference);
      if (opening !== runtime.current.opening || runtime.current.disposed) {
        return;
      }
      setActivity(nextActivity);
      setCaseCallToDocumentId(null);
      setCalendlyEvent(event);
      setCallProduct(product);
      setRecordingEnabled(getUserConsent(product.customer, "callRecordingAndSummary") !== null
        ? hasUserConsent(product.customer, "callRecordingAndSummary")
        : hasLegacyUserConsent(event, "callRecordingAndSummary"));
      runtime.current.onEnd = callback ?? null;
      setPhoneError("");
      setCallModalOpen(true);
      await createTwilioDevice();
    } catch (error) {
      if (!runtime.current.disposed && opening === runtime.current.opening) {
        setPhoneError(getTwilioErrorMessage(error));
        setStatus(translate("call.callStatus.failed"));
      }
    }
  };

  const openCaseCallModal = async () => {
    if (runtime.current.session) {
      setCallModalOpen(true);
      return;
    }
    if (!product) {
      return;
    }
    const opening = ++runtime.current.opening;
    setActivity(null);
    setCalendlyEvent(null);
    setCallProduct(product);
    setCaseCallToDocumentId(null);
    setRecordingEnabled(false);
    setPhoneError("");
    runtime.current.onEnd = null;
    setCallModalOpen(true);
    try {
      await createTwilioDevice();
    } catch (error) {
      if (!runtime.current.disposed && opening === runtime.current.opening) {
        setPhoneError(getTwilioErrorMessage(error));
      }
    }
  };

  const closeCallModal = () => {
    if (!runtime.current.session) {
      runtime.current.opening += 1;
      setActivity(null);
      setCalendlyEvent(null);
      setCallProduct(null);
      setCaseCallToDocumentId(null);
      runtime.current.device?.destroy();
      runtime.current.device = null;
      setDevice(null);
    }
    setCallModalOpen(false);
  };

  const loadPhoneCall = async (session: CallSession): Promise<Pick<PhoneCall, "id" | "recordingEnabled">> => {
    if (session.phoneCall) {
      return session.phoneCall;
    }
    const callSid = session.call?.parameters.CallSid;
    if (!callSid) {
      throw new Error("Die Verbindung ist noch nicht bereit.");
    }
    const result = await fetchCollection("phone_calls", {
      callSid,
      case: `/abstract_cases/${session.product.id}`,
    }) as HydraCollectionResult<PhoneCall>;
    const saved = result["hydra:member"][0];
    if (!saved) {
      throw new Error("Der Anruf konnte noch nicht geladen werden. Bitte erneut versuchen.");
    }
    session.phoneCall = saved;
    return saved;
  };

  const makeCall = async (phoneNumber: string, recipientIdentifier: string | null = null) => {
    if (runtime.current.session || !callProduct) {
      return;
    }
    const session: CallSession = {
      call: null, cancelled: false, failed: false, releaseLock: null,
      onEnd: runtime.current.onEnd, product: callProduct, phoneCall: null, recordingStopped: false,
      isCaseCall: calendlyEvent === null,
      finished: false, opening: runtime.current.opening,
    };
    runtime.current.session = session;
    setIsConnecting(true);
    setPhoneError("");
    try {
      const acquired = await new Promise<boolean>((resolve, reject) => {
        navigator.locks.request("legalbird-voice-call", { ifAvailable: true }, async (lock) => {
          if (!lock) {
            resolve(false);
            return;
          }
          await new Promise<void>((release) => {
            session.releaseLock = release;
            resolve(true);
          });
        }).catch(reject);
      });
      if (!acquired) {
        throw new Error("In einem anderen Tab läuft bereits ein Anruf.");
      }
      if (session.cancelled) {
        finishCall(session);
        return;
      }
      if (session.isCaseCall) {
        session.phoneCall = await apiPost<Pick<PhoneCall, "id" | "recordingEnabled">>("/twilio/prepare_case_call", {
          case: `/abstract_cases/${session.product.id}`,
          recipientIdentifier,
          toNumber: phoneNumber,
          recordingEnabled,
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.collections("phone_calls") });
        if (session.cancelled || runtime.current.disposed) {
          finishCall(session);
          return;
        }
        setRecordingEnabled(session.phoneCall.recordingEnabled);
      }
      const currentDevice = await createTwilioDevice();
      if (session.cancelled) {
        finishCall(session);
        return;
      }
      setStatus(session.isCaseCall ? "Verbindung wird aufgebaut …" : `Wähle ${phoneNumber}...`);
      const params: Record<string, string> = session.phoneCall ? { phoneCallId: session.phoneCall.id.toString() } : {
        To: phoneNumber,
        caseId: session.product.id.toString(),
        lbProductClass: session.product.backofficeCase.lbProductClass,
        calendlyEventId: calendlyEvent!.id.toString(),
        recordingEnabled: recordingEnabled.toString(),
      };
      const activeCall = await currentDevice.connect({ params });
      session.call = activeCall;
      if (session.cancelled || runtime.current.disposed) {
        activeCall.disconnect();
        finishCall(session);
        return;
      }
      const accepted = async () => {
        if (runtime.current.session !== session) {
          return;
        }
        setStatus("Im Gespräch");
        setMuted(activeCall.isMuted());
        try {
          const saved = session.isCaseCall && session.phoneCall
            ? await apiGet<PhoneCall>(`/phone_calls/${session.phoneCall.id}`)
            : await loadPhoneCall(session);
          session.phoneCall = saved;
          if (runtime.current.session === session && !session.recordingStopped) {
            setRecordingEnabled(saved.recordingEnabled);
          }
        } catch (error) {
          if (runtime.current.session === session) {
            setPhoneError(getTwilioErrorMessage(error));
          }
        }
      };
      activeCall.on("accept", accepted);
      activeCall.on("ringing", () => {
        if (runtime.current.session === session) {
          setStatus("Klingelt");
        }
      });
      activeCall.on("error", (error: unknown) => {
        if (runtime.current.session === session) {
          session.failed = true;
          setPhoneError(getTwilioErrorMessage(error));
          setStatus(translate("call.callStatus.failed"));
          finishCall(session);
          activeCall.disconnect();
        }
      });
      activeCall.on("disconnect", () => finishCall(session));
      activeCall.on("cancel", () => finishCall(session));
      activeCall.on("reject", () => finishCall(session));
      activeCall.on("mute", (isMuted: boolean) => {
        if (runtime.current.session === session) {
          setMuted(isMuted);
        }
      });
      setCall(activeCall);
      setIsConnecting(false);
      if (activeCall.status() === Call.State.Open) {
        await accepted();
      }
    } catch (error) {
      if (!session.cancelled && !runtime.current.disposed) {
        session.failed = true;
        setPhoneError(getTwilioErrorMessage(error));
        setStatus(translate("call.callStatus.failed"));
      }
      finishCall(session);
    }
  };

  const hangUp = () => {
    const session = runtime.current.session;
    if (session) {
      session.cancelled = true;
      session.call?.disconnect();
    }
  };

  const cancelRecording = async () => {
    const session = runtime.current.session;
    if (!session) {
      setRecordingEnabled(false);
      return;
    }
    setIsStoppingRecording(true);
    try {
      const saved = await loadPhoneCall(session);
      await apiPost("/twilio/cancel_recording", { phoneCallId: saved.id });
      session.recordingStopped = true;
      if (runtime.current.session === session) {
        setRecordingEnabled(false);
        setPhoneError("");
      }
    } catch {
      if (runtime.current.session === session) {
        setPhoneError("Aufzeichnungsstopp nicht bestätigt. Bitte erneut versuchen oder das Gespräch beenden.");
      }
    } finally {
      setIsStoppingRecording(false);
    }
  };

  const applyMute = (shouldMute: boolean) => {
    const activeCall = runtime.current.session?.call;
    if (activeCall?.status() === Call.State.Open) {
      activeCall.mute(shouldMute);
      setMuted(activeCall.isMuted());
    }
  };

  return (
    <CallContext.Provider value={{
      status, phoneError, makeCall, hangUp, cancelRecording, recordingEnabled, setRecordingEnabled,
      isConnecting, isStoppingRecording, call, device, callModalOpen, openCallModal, closeCallModal,
      openCaseCallModal, caseCallToDocumentId,
      activity, calendlyEvent, callProduct, muted,
      mute: () => applyMute(true), unmute: () => applyMute(false), toggleMute: () => applyMute(!muted),
    }}>
      {children}
    </CallContext.Provider>
  );
};

export const useOptionalCall = () => useContext(CallContext);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return context;
};
